/**
 * @module app.cd
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { IContactLogConfig } from 'app/core/models/essette-interfaces';
import { IPopup } from 'app/shared/elements/popup-base';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { ContactLogService } from 'app/core/services/contact-log-service';
import { MemberService } from 'app/cd/services/member-service';
import { apiCall } from 'app/shared/lib/api/api';
import { validate } from 'app/shared/elements/ess-rule';
import { ContactLogGrid } from 'app/core/components/contact-log/contact-log-grid';
import * as CD from 'app/cd/models/essette-api';
import { IDialogOptions, IModalData, openGlobalModal } from 'app/shared/elements/ess-global-modal';
import { guid } from 'app/shared/lib/guid';
import { Toaster } from 'app/shared/lib/toaster';
import { forceNull } from 'app/shared/lib/utilities';

@autoinject()
export class ContactLog extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable({ defaultBindingMode: bindingMode.toView })
    public targetId: string | null = null;

    @bindable({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public memberId: string | null = null;

    @bindable({ defaultBindingMode: bindingMode.oneTime })
    public contactLogConfig: IContactLogConfig = {};

    @bindable.boolean()
    protected showHouseholdFields: boolean = false;

    protected member: CD.Member | null = null;

    protected editedEmail?: string | null = null;

    protected validateForm = validate;
    protected editEmailFormIsValid: boolean = false;


    @bindable({ defaultBindingMode: bindingMode.toView })
    public collapseNonContactLog: boolean = false;
    @bindable({ defaultBindingMode: bindingMode.toView })
    public readonlyContactLogGrid: boolean = false;

    ////****************** Element References ***************************************////
    private $editEmailModal!: IPopup;
    protected $editEmailForm!: IForm;
    protected $contactLogGrid!: ContactLogGrid;

    protected id: guid = guid();


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly memberService: MemberService,
        private readonly contactLogService: ContactLogService,
        private readonly toaster: Toaster,
        ...rest: any[]) {
        super(...rest);
    }

    protected async activate(model: IContactLogModelData): Promise<void> {
        if(!model) { return; }

        if(model.member?.id) {
            this.memberId = model.member.id;
        }

        this.targetId = model.targetId;
        this.contactLogConfig = model.contactLogConfig;
        this.showHouseholdFields = model.showHouseholdFields || false;
    }

    protected bind(): void {
        this.contactLogService.configure(this.contactLogConfig);
        this.loadMember();
    }


    ////****************** Events ***************************************************////


    ////****************** Functions ************************************************////
    public reload(): void {
        this.loadMember();
    }

    protected clearEdits() {
        this.editedEmail = null;
    }

    protected openEditEmail(): void {
        this.editedEmail = this.member?.emailAddress;
        this.$editEmailModal.open();
    }

    protected closeEditEmailModal(): void {
        this.$editEmailModal.close();
        this.clearEdits();
    }

    protected async submitEditEmail(): Promise<void> {
        await this.saveContact();
        this.$editEmailModal.close();
        // Reload Member and clear edits on modal close
        await this.loadMember();
        this.clearEdits();
    }

    public reloadContactLog(): void {
        if(this.$contactLogGrid) {
            this.$contactLogGrid.reload()
        }
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadMember(): Promise<void> {
        if(!this.memberId) return;

        const includes: string[] = [
            'address.home',
            'address.mailing',
            'address.work',
            'contact.preference',
            'phoneNumber.cell',
            'phoneNumber.fax',
            'phoneNumber.home',
            'phoneNumber.work',
        ];

        try {
            this.state.beginLoad();
            let response = await this.memberService.loadMember(this.memberId, includes);
            this.member = response.results[0];
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async saveContact(): Promise<boolean> {
        if(!this.memberId) return false;

        try {
            this.state.beginSave();
            const response = await this.memberService.updateMemberEmail(this.memberId, forceNull(this.editedEmail));
            this.toaster.standard.saved('cd:member.emailAddress.$', response.isSuccessful);
            return response.isSuccessful;
        } catch(error) {
            throw error;
        } finally {
            this.state.endSave();
        }
    }
}

export interface IContactLogModelData {
    member: CD.Member,
    contactLogConfig: IContactLogConfig,
    targetId: string,
    readonly?: boolean,
    showHouseholdFields?: boolean
}

/**
 * Opens the contact log modal with the global dialog
 * @param member The member to use
 * @param contactLogConfig The contact log configuration data to use
 * @param targetId The target ID to use (e.g., case ID, auth ID, etc.)
 * @param readonly Readonly boolean
 */
export function openContactLog(member: CD.Member, contactLogConfig: IContactLogConfig, targetId: string, readonly: boolean = false, showHouseholdFields: boolean = false): void {
    if(!member || !member.id) {
        throw new Error('Member is missing or invalid');
    }

    if(!targetId) {
        throw new Error('TargetId is missing or invalid');
    }

    if(!contactLogConfig.targetType || !contactLogConfig.rollupConfig || !targetId) {
        throw new Error('Config is missing or invalid');
    }

    let dialogOptions: IDialogOptions = {
        width: '10',
        height: '11',
        helpTopic: `${contactLogConfig.targetType.toLocaleLowerCase()}.contact-log`,
        title: {
            key: 'cd:routes.contactLog.$title',
            replace: { display: member.$display }
        },
        print: true,
        popout: {
            route: `${contactLogConfig.targetType.toLocaleLowerCase()}.contact-log`,
            params: {
                memberId: member.id,
                targetId: targetId
            }
        }
    };

    switch(contactLogConfig.targetType) {
        case 'CM.Case':
            Object.assign(dialogOptions.popout!.params!, { caseId: targetId });
            break;
        case 'UM.Auth':
            Object.assign(dialogOptions.popout!.params!, { authId: targetId });
            break;
    }

    const modelData: IContactLogModelData = {
        member: member,
        contactLogConfig: contactLogConfig,
        targetId: targetId,
        readonly: readonly,
        showHouseholdFields: showHouseholdFields
    };

    const modalData: IModalData = {
        componentId: `app/cd/components/contact-log/contact-log`,
        modelData: modelData,
        dialogOptions: dialogOptions
    };

    openGlobalModal(modalData);
}
